/**
 * The interface.
 *
 * Amassa wears the Quotinc-family interface (paper and glass, sienna lead,
 * cobalt counter) for everyone since 2026-10-01; the original "Woven
 * Signal" interface is retired. It is painted by ONE attribute,
 * `<html data-skin="quotinc">`, and `styles/skin-quotinc.css` does the rest:
 * it re-values the base tokens (tokens.css / App.css are still the layer
 * underneath) and styles the shell chrome.
 *
 * The attribute is painted before React boots by the script in index.html,
 * and kept right on navigation by PublicPagesGate.
 *
 * Guest pages (the transfer page, the guides) are never painted: they carry
 * their own guest design and do not read the app tokens at all.
 */

export const SKIN_KEY = 'hub.skin';
export const SKIN_NAME = 'quotinc';
const THEME_KEY = 'hub.theme';

/** Keep in step with the pre-boot script in index.html. */
const GUEST_PATH = /^\/((cs\/)?\d{4,}|(guide|places|tours|gifts|gift-list|pickup|transfers|p)(\/.*)?)\/?$/;

export function isGuestPath(pathname: string): boolean {
  return GUEST_PATH.test(pathname);
}

/** Is the interface painted right now (false on a guest page). */
export function skinActive(): boolean {
  return typeof document !== 'undefined' && document.documentElement.dataset.skin === SKIN_NAME;
}

/**
 * Paint (or clear) the attribute for the current path. The first time a
 * browser meets this interface it starts in light (the interface is paper
 * first); after that the theme toggle is the person's own.
 */
export function paintSkin(): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  const painted = !isGuestPath(window.location.pathname);
  if (painted) {
    root.dataset.skin = SKIN_NAME;
    try {
      if (window.localStorage.getItem(SKIN_KEY) !== SKIN_NAME) {
        window.localStorage.setItem(SKIN_KEY, SKIN_NAME);
        window.localStorage.setItem(THEME_KEY, 'light');
        root.dataset.theme = 'light';
      }
    } catch {
      /* storage blocked: the attribute still paints this session */
    }
  } else {
    delete root.dataset.skin;
  }
  paintFavicon(painted);
}

const FAVICON_GUEST = '/favicon.svg';
const FAVICON_SKIN = '/amassa-favicon-v3.png';

/** The tab icon: the Amassa logo (v3); guest pages keep the default /favicon.svg. */
function paintFavicon(skin: boolean): void {
  const link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (!link) return;
  const want = skin ? FAVICON_SKIN : FAVICON_GUEST;
  if (link.getAttribute('href') === want) return;
  link.setAttribute('type', skin ? 'image/png' : 'image/svg+xml');
  link.setAttribute('href', want);
}
